"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";

export default function LandingPage() {
  const router = useRouter();
  const [marketCount, setMarketCount] = useState(0);

  useEffect(() => {
    // Capture ?ref=CODE from the URL and persist before the user navigates away
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("blackdoor_ref_v1", ref.toUpperCase().trim());

    // Fetch real market count from Redis (works on every device, not just admin's browser)
    fetch("/api/crm/markets")
      .then(r => r.json())
      .then(d => {
        const count = (d.markets ?? []).filter(
          (m: any) => !m.resolved && !m.hidden && !m.cancelled && m.trading
        ).length;
        setMarketCount(count);
      })
      .catch(() => {});
  }, []);

  return (
    <main className="relative flex h-screen w-screen items-center justify-center overflow-hidden bg-white">
      {/* Subtle scanline texture */}
      <div className="pointer-events-none absolute inset-0 z-10"
        style={{ backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.025) 2px,rgba(0,0,0,0.025) 4px)" }}
      />

      {/* Center glow */}
      <div className="pointer-events-none absolute inset-0 z-10">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-black opacity-[0.025] blur-[100px]" />
      </div>

      {/* Hero */}
      <div className="relative z-20 flex flex-col items-center gap-10 text-center px-4">

        {/* Logo + title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center gap-6"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo.jpeg"
            alt="BlackDoor"
            width={110}
            height={110}
            className="select-none rounded-2xl"
          />
          <div className="flex flex-col items-center gap-1">
            <h1
              className="text-6xl md:text-8xl font-black tracking-tighter text-black leading-none"
              style={{ textShadow: "0 0 40px rgba(0,0,0,0.08)" }}
            >
              BLACKDOOR
            </h1>
            <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-black/5 px-4 py-1.5 text-xs font-medium text-black/50 tracking-widest uppercase mt-2">
              On-chain · Arbitrum One · USDC
            </div>
          </div>
          <p className="max-w-xs text-sm text-black/40 leading-relaxed">
            Prediction markets anchored to the physical world.<br />
            Every event lives on the globe where it happens.
          </p>
        </motion.div>

        {/* Power button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 200, damping: 22 }}
          className="relative flex items-center justify-center"
        >
          <motion.div
            className="absolute rounded-full border border-black/12"
            animate={{ scale: [1, 1.35, 1], opacity: [0.4, 0, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{ width: 120, height: 120 }}
          />
          <motion.div
            className="absolute rounded-full border border-black/7"
            animate={{ scale: [1, 1.6, 1], opacity: [0.25, 0, 0.25] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            style={{ width: 120, height: 120 }}
          />
          <motion.button
            onClick={() => router.push("/home")}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            className="relative z-10 flex h-22 w-22 items-center justify-center rounded-full border-2 border-black/75 bg-black/4 hover:bg-black/10 transition-colors"
            style={{ boxShadow: "0 0 40px rgba(0,0,0,0.06), inset 0 0 20px rgba(0,0,0,0.02)" }}
          >
            <svg viewBox="0 0 32 32" fill="none" className="w-9 h-9" strokeLinecap="round">
              <path d="M16 5v8" stroke="black" strokeWidth="2.2" />
              <path d="M10 8.8A10 10 0 1 0 22 8.8" stroke="black" strokeWidth="2.2" />
            </svg>
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="flex items-center gap-8 text-xs text-black/30"
        >
          <Stat label="Markets" value={String(marketCount)} />
          <div className="h-3 w-px bg-black/10" />
          <Stat label="Chain" value="Arbitrum" />
          <div className="h-3 w-px bg-black/10" />
          <Stat label="Token" value="USDC" />
        </motion.div>

      </div>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-sm font-bold text-black">{value}</span>
      <span className="uppercase tracking-wider">{label}</span>
    </div>
  );
}
